import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Image, Button } from 'react-bootstrap';
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react';
import { Navigation, Pagination, Mousewheel, Keyboard } from 'swiper';

import useLocale from 'hooks/useLocale';
//import useLeasingValue from 'hooks/useLeasingValue';
import useTotalPrice from 'hooks/useTotalPrice';

import slugify from 'utils/slugify';

import { setBuilderData, setErrorData } from 'store/entities/data';
import { setConfig, setBody, setEngine, setSpecification } from 'store/entities/config';

import { CarPartCard } from 'components/Cards';
import { TechDataModal } from 'components/modals';

import LocalizedLink from 'components/LocalizedLink';
import RewindButton from 'components/RewindButton';
import BottomBar from 'components/BottomBar';
import Price from 'components/Price';
import Disclaimer from 'components/Price/Disclaimer';

import LoadingSpinner from 'components/LoadingSpinner';
import NotFound from 'components/NotFound';

import PlaceholderCar from 'assets/images/placeholder-car.png';
import { ReactComponent as ArrowLeft } from 'assets/images/icons/ArrowLeft.svg';
import { ReactComponent as ArrowRight } from 'assets/images/icons/ArrowRight.svg';

import { useBuilderDataRequest, useOptionsDataRequest } from 'api/requests';

const CarPartSelection = () => {
    const { t, i18n } = useTranslation();
    const locale = useLocale();
    const dispatch = useDispatch();

    const [viewModal, setViewModal] = React.useState(null);

    const { model: modelId, specification, gradeId, body, engine, leasingPeriod } = useSelector(state => state.config);
    const { modelsData, eicsData, builderData, errorData } = useSelector(state => state.data);

    const ENV = window.ENV[window.location.hostname] || window.ENV.default;

    //const { mileagePrice } = useLeasingValue();
    const totalPrice = useTotalPrice();

    const currentModel = modelsData.find(item => item.id === modelId);
    const modelBackTo = currentModel ? `/${slugify(currentModel.name)}` : `/grade?id=${modelId}`;

    //const builderData = eicsData[gradeId].find(item => item.eic === specification);
    const bodyData = [...(eicsData[gradeId] || [])].sort((a, b) => // sort asc by price
        a.price - b.price
      ).reduce((mapped, item) => { // group engines by body
        mapped[item.body.id] = mapped[item.body.id] || {...item.body, engines: []};
        if (!mapped[item.body.id].engines.some(engine => engine.eic === item.eic)) { // check duplicates by eic value
          mapped[item.body.id].engines.push({
                ...item.engine,
                eic: item.eic,
                title: item.name,
                price: item.price,
                leasing: item.leasing,
                img: item.exterior?.pictures?.main || PlaceholderCar,
                thumb: item.grade.thumb,
                options: item.options,
                techdata: item.techdata
            });
        }
        return mapped;
      }, {});

    const image = builderData.grade?.thumb || builderData.exterior?.pictures?.main || PlaceholderCar;

    const engines = bodyData[body]?.engines || [];
    const bodies = Object.keys(bodyData || {});

    const gradeToken = `grade_features_${slugify(builderData?.model?.name, '_')}_${slugify(builderData?.grade?.code, '_')}`;

    const handleSelectEngine = (engineId, eic) => {
        dispatch(setEngine(engineId));
        dispatch(setSpecification(eic));
    };

    const handleSelectBody = (bodyId, engineId, eic) => {
        dispatch(setBody(bodyId));
        if (engineId || eic) handleSelectEngine(engineId, eic);
    };

    const handleModalShow = (eic, type) => {
        setViewModal({ eic, type });
    };
    const handleModalHide = (eic, type) => {
        setViewModal(null);
    };

    const filterExteriors = (data = {}, exteriors = []) => {
        //const isTwoTone = /2-tone/i.test(current.categoryName);
        //const isStandard = /standard|solid/i.test(current.categoryName);
        //const price = isTwoTone ? twotoneValue : (!isStandard ? metallicValue : 0);
        //return exteriors
        //    .filter(item => (allowed || []).length ? allowed.includes(item.code) : isTwoTone === regex.test(item.name))
        //    .map(item => ({ ...item, leasingPrice: item.price ? price : 0 }));


        const leasing = data.leasing || {};
        const allowed = leasing.exteriorCodes || [];
        const period = leasing.period?.find(item => item.value === leasingPeriod) || {};
        const twotoneValue = period.pearlTwoTone?.price || 0;
        const metallicValue = period.metallicColor?.price || 0;



        const getExteriorPrice = (category) => {
            const isTwoTone = /2-tone/i.test(category);
            const isStandard = /standard|solid/i.test(category);
            return isTwoTone ? twotoneValue : (!isStandard ? metallicValue : 0);
        };
        return exteriors
            .filter(item => !allowed.length || allowed.includes(item.code))
            .map(item => ({ ...item, leasingPrice: getExteriorPrice(item.categoryName) }));
    };

    const dataOptions = useOptionsDataRequest({
        params: {
            specification,
            params: {
                brand: ENV.BRAND_NAME,
                language: locale
            }
        },
        config: {
            enabled: false,
        },
        success: (result) => {
            const data = {...(eicsData[gradeId] || []).find(item => item.eic === specification)};
            data.exteriors = filterExteriors(data, result.data?.exteriors);
            // if default color is not in list - choose first available
            const colors = data.exteriors.map(item => item.code);
            let colorCode = data.exterior?.code;
            if (colors && !colors.includes(colorCode)) colorCode = colors[0];

            dispatch(setBuilderData(data));
            dispatch(setConfig({
                code: data.model?.code,
                colorCode,
                interior: data.interior?.code,
                wheels: data.wheels?.code
            }));
            dispatch(setErrorData({}));
        },
        error: (error) => {
            dispatch(setErrorData({
                type: 'builder-data',
                message: error.message,
                refetch: true
            }));
        }
    });

    React.useEffect(() => {
        specification ? dataOptions.refetch() : dispatch(setErrorData({
            type: 'model',
            message: t('error_missing_id', 'Model ID or model CODE is missing'),
            redirect: '/'
        }));
    }, [specification]);

    const swiperProps = {
        className: 'card-swiper carpart-swiper',
        cssMode: true,
        slidesPerView: 'auto',
        navigation: {
            prevEl: '.swiper-button-prev',
            nextEl: '.swiper-button-next',
        },
        pagination: {
            clickable: true,
        },
        mousewheel: true,
        keyboard: true,
        modules: [Navigation, Pagination, Mousewheel, Keyboard]
    };

    return (<>
        {dataOptions.isFetching && (
            <LoadingSpinner />
        )}
        {!errorData?.type ? (<>
            {builderData.eic && (
                <section className="c-section">
                    <div className="c-vehicle">
                        <Container className="c-vehicle__body">
                            <div className="c-vehicle__caption c-caption">
                                <h2 className="c-caption__title">{builderData.name}</h2>
                                <p className="c-caption__price c-price">
                                    <span className="c-price__title">{t('total_price')}</span>
                                    <strong className="c-price__value">
                                        <Price price={totalPrice} />
                                        <Disclaimer />
                                    </strong>
                                    <span className="c-price__title">{t('leasing_per_month')}</span>
                                </p>
                                <p className="c-caption__btn">
                                    <RewindButton href={modelBackTo} text={t('switch_vehicle')} />
                                </p>
                            </div>
                            <div className="c-vehicle__info c-info">
                                <h5 className="c-info__title">{t('equipment')}</h5>
                                <div className="c-info__list">
                                    {t(gradeToken)}
                                </div>
                                <p className="c-info__btn">
                                    <Button variant="link" onClick={() => handleModalShow(specification, 'equipment')}>
                                        {t('view_more_btn')}
                                    </Button>
                                </p>
                            </div>
                        </Container>
                        <figure className="c-vehicle__img">
                            <Image src={image} />
                        </figure>
                    </div>
                </section>
            )}
            <section className="c-section c-engines">
                <Container>
                    {bodies.length > 1 && (<>
                        <h4 className="c-section__subtitle">{t('choose_body')}</h4>
                        <Swiper {...swiperProps}>
                            {bodies.map(key => (
                                <SwiperSlide key={bodyData[key].id}>
                                    <CarPartCard
                                        bodyData={bodyData[key]}
                                        onSelect={handleSelectBody}
                                        onDetails={() => handleModalShow(bodyData[key].eic, 'techdata')}
                                        buttonText="choose_body_button"
                                    />
                                </SwiperSlide>
                            ))}
                            <button className="swiper-button swiper-button-prev" type="button"><ArrowLeft /></button>
                            <button className="swiper-button swiper-button-next" type="button"><ArrowRight /></button>
                        </Swiper>
                    </>)}
                    <div className="c-section__head">
                        <h4 className="c-section__subtitle">{t('choose_engine')} </h4>
                        <LocalizedLink to="/assemble" className="btn btn-primary">
                            {t('continue_button')}
                            <ArrowRight className="btn-ico btn-ico-right" />
                        </LocalizedLink>
                    </div>
                    {engines.length ? (
                        <Swiper {...swiperProps}>
                            {engines.map((engine, index) => (
                                <SwiperSlide key={`${engine.id}-${index}`}>
                                    <CarPartCard
                                        engineData={engine}
                                        onSelect={handleSelectEngine}
                                        onDetails={() => handleModalShow(engine.eic, 'techdata')}
                                        buttonText="choose_engine_button"
                                    />
                                </SwiperSlide>
                            ))}
                            <button className="swiper-button swiper-button-prev" type="button"><ArrowLeft /></button>
                            <button className="swiper-button swiper-button-next" type="button"><ArrowRight /></button>
                        </Swiper>
                    ) : (
                        <h5 className="c-section__subheader">{t('error_engines_not_found', 'No engines found')}</h5>
                    )}
                </Container>
            </section>
            <BottomBar action={
                <LocalizedLink to="/assemble" className="btn btn-link">
                    {t('continue_button')}
                    <ArrowRight className="btn-ico btn-ico-right" />
                </LocalizedLink>
            }/>
            <TechDataModal show={!!viewModal} onClose={handleModalHide} data={viewModal} />
        </>) : (
            <NotFound error={errorData} />
        )}
    </>);
};

export default CarPartSelection;
